import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { type MessageKey, useT } from "../i18n";
import { colorHex } from "../lib/colors";
import { renderInline } from "../lib/markdown";
import { fold } from "../lib/tasks";
import { useApp, useSyncState } from "../store";
import { TaskEditor } from "../tasks/TaskEditor";
import { openTask } from "../tasks/TaskRow";
import { IconHash, IconInbox, IconSearch } from "../ui/icons";
import { Modal, ModalHeader } from "../ui/primitives";

export function QuickAddModal() {
  const qa = useApp((s) => s.quickAdd);
  const set = useApp((s) => s.set);
  const t = useT();
  if (!qa.open) return null;
  const close = () => set({ quickAdd: { open: false } });
  const d = qa.defaults;
  return (
    <Modal onClose={close} label={t("addTask")}>
      <TaskEditor
        variant="modal"
        closeOnSubmit
        defaults={{
          projectId: d?.projectId,
          sectionId: d?.sectionId,
          parentId: d?.parentId,
          labels: d?.label ? [d.label] : undefined,
          due: d?.dueString ? { date: d.dueString } : undefined,
        }}
        onClose={close}
      />
    </Modal>
  );
}

export function SearchModal() {
  const open = useApp((s) => s.searchOpen);
  if (!open) return null;
  return <SearchBody />;
}

function SearchBody() {
  const state = useSyncState();
  const set = useApp((s) => s.set);
  const navigate = useNavigate();
  const t = useT();
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(0);
  const close = () => set({ searchOpen: false });
  const results = useMemo(() => {
    const needle = fold(q.trim());
    if (!needle) return [];
    const projects = Object.values(state.projects)
      .filter((p) => fold(p.inbox_project ? `${p.name} ${t("inbox")}` : p.name).includes(needle))
      .slice(0, 8)
      .map((p) => ({ kind: "project" as const, id: p.id, project: p }));
    const tasks = Object.values(state.items)
      .filter(
        (i) => !i.checked && state.projects[i.project_id] && fold(`${i.content} ${i.description}`).includes(needle),
      )
      .slice(0, 30)
      .map((i) => ({ kind: "task" as const, id: i.id, item: i }));
    return [...projects, ...tasks];
  }, [q, state, t]);
  const go = (i: number) => {
    const r = results[i];
    if (!r) return;
    close();
    if (r.kind === "project") navigate(r.project.inbox_project ? "/app/inbox" : `/app/project/${r.id}`);
    else openTask(navigate, r.id);
  };
  return (
    <Modal onClose={close} className="search-modal" label={t("search")}>
      <div style={{ display: "flex", alignItems: "center", paddingLeft: 12 }}>
        <IconSearch size={20} />
        <input
          className="search-input"
          autoFocus
          placeholder={t("searchPlaceholder")}
          value={q}
          data-testid="search-input"
          onChange={(e) => {
            setQ(e.target.value);
            setSel(0);
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setSel(Math.min(sel + 1, results.length - 1));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setSel(Math.max(sel - 1, 0));
            } else if (e.key === "Enter") {
              e.preventDefault();
              go(sel);
            }
          }}
        />
      </div>
      <div className="search-results" data-testid="search-results">
        {q && !results.length && <div className="empty-state">{t("emptySearch")}</div>}
        {results.map((r, i) => (
          <button
            key={`${r.kind}${r.id}`}
            type="button"
            className={`menu-item${i === sel ? " active" : ""}`}
            onMouseEnter={() => setSel(i)}
            onClick={() => go(i)}
            data-testid="search-result"
          >
            {r.kind === "project" ? (
              <>
                {r.project.inbox_project ? (
                  <IconInbox size={18} />
                ) : (
                  <span style={{ color: colorHex(r.project.color), display: "inline-flex" }}>
                    <IconHash size={18} />
                  </span>
                )}
                <span>{r.project.inbox_project ? t("inbox") : r.project.name}</span>
              </>
            ) : (
              <>
                <span className="checkbox" style={{ marginTop: 0, width: 14, height: 14 }} />
                <span
                  style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                >
                  {renderInline(r.item.content, "s", true)}
                </span>
                <span className="muted small">
                  {state.projects[r.item.project_id]?.inbox_project
                    ? t("inbox")
                    : state.projects[r.item.project_id]?.name}
                </span>
              </>
            )}
          </button>
        ))}
      </div>
    </Modal>
  );
}

const SHORTCUTS: [string, MessageKey | string][] = [
  ["q", "addTask"],
  ["/  f  ⌘K", "search"],
  ["m", "openSidebar"],
  ["g i", "inbox"],
  ["g t", "today"],
  ["g u", "upcoming"],
  ["g f", "filtersLabels"],
  ["j / k", "↓ / ↑"],
  ["Enter", "edit"],
  ["e", "completed"],
  ["1 – 4", "priority"],
  ["Esc", "close"],
  ["⌘ Enter", "save"],
];

export function ShortcutsModal() {
  const open = useApp((s) => s.shortcutsOpen);
  const set = useApp((s) => s.set);
  const t = useT();
  if (!open) return null;
  const close = () => set({ shortcutsOpen: false });
  return (
    <Modal onClose={close} center label={t("shortcuts")}>
      <ModalHeader title={t("shortcuts")} onClose={close} />
      <div className="modal-body">
        <table className="table">
          <tbody>
            {SHORTCUTS.map(([k, label]) => (
              <tr key={k}>
                <td>{label.includes(" ") || label.includes("/") ? label : t(label as MessageKey)}</td>
                <td style={{ textAlign: "right" }}>
                  {k.split(/\s+/).map((part) => (
                    <span key={part} className="kbd" style={{ marginLeft: 4 }}>
                      {part}
                    </span>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}

export function Toasts() {
  const toasts = useApp((s) => s.toasts);
  const dismiss = useApp((s) => s.dismissToast);
  const t = useT();
  const label = (m: string) => {
    try {
      return t(m as MessageKey) ?? m;
    } catch {
      return m;
    }
  };
  return (
    <div className="toasts" aria-live="polite">
      {toasts.map((x) => (
        <div className="toast" key={x.id} role="status" data-testid="toast">
          <span style={{ flex: 1 }}>{label(x.message)}</span>
          {x.action && (
            <button
              type="button"
              data-testid="toast-action"
              onClick={() => {
                x.action?.run();
                dismiss(x.id);
              }}
            >
              {label(x.action.label)}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
